import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  styled,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { changePassword } from "@services/auth";
import { useNavigate } from "react-router-dom";

const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  padding: "12px 64px",
  height: "124px",
  display: "flex",
  alignItems: "end",
  gap: "8px",
  position: "sticky",
  top: 0,
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    padding: "12px 24px",
    height: "96px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 16px",
    height: "80px",
  },
}));

const HeaderTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "28px",
  fontWeight: 500,
  lineHeight: "1.5",
  color: "#262626",
  letterSpacing: "-1px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "22px",
  },
}));

const InputContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4.5px",
  width: "100%",
});

const InputLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    height: "42px",
    borderRadius: "4px",
    "& fieldset": {
      borderColor: "#e6fafc",
    },
    "&:hover fieldset": {
      borderColor: "#bfe5ec",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0a829d",
    },
    boxShadow:
      "inset 0px 0px 8px 0px rgba(0,108,116,0.12), inset 0px 0px 4px 0px rgba(34,110,127,0.25)",
  },
  "& .MuiOutlinedInput-input": {
    padding: "11px 14px",
    fontSize: "14px",
    fontFamily: "Rethink Sans, sans-serif",
  },
});

const PasswordStrengthContainer = styled(Box)({
  display: "flex",
  gap: "2px",
  alignItems: "center",
  padding: "2px",
  width: "100%",
});

const PasswordStrengthBar = styled(Box)<{ isActive: boolean }>(
  ({ isActive }) => ({
    flex: 1,
    height: "4px",
    backgroundColor: isActive ? "#0a829d" : "#e5e7eb",
    borderRadius: "16px",
  })
);

const FormContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  alignItems: "left",
  paddingTop: "8px",
  flex: 1,
  width: "100%",
  maxWidth: "500px",
});

const PasswordSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
});

const SingleInputSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
});

const ContinueButton = styled(Button)<{ isActive: boolean }>(
  ({ isActive, theme }) => ({
    background: isActive
      ? "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)"
      : "#ddf0f4",
    border: "1px solid #ffffff",
    borderRadius: "48px",
    height: "48px",
    width: "25%",
    padding: "12px 24px",
    textTransform: "none",
    fontFamily: "Rethink Sans, sans-serif",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "20px",
    color: isActive ? "#ffffff" : "#bad6dc",
    boxShadow: isActive
      ? "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)"
      : "none",
    "&:hover": {
      background: isActive
        ? "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)"
        : "#ddf0f4",
    },
    "&:disabled": {
      background: "#ddf0f4",
      color: "#bad6dc",
    },
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  })
);

const PageContainer = styled(Box)({
  background: "#fdffff",
  borderRadius: "12px",
  height: "99%",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #99d4e1",
  boxShadow: "8px 2px 58.4px 0px inset rgba(191, 242, 255, 0.2)",
  position: "relative",
});

const ContentSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "18px 48px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  overflowY: "auto",
  overflowX: "hidden",
  [theme.breakpoints.down("md")]: {
    padding: "18px 24px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "18px 12px",
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  [theme.breakpoints.down("sm")]: {
    gap: "16px",
  },
}));

const FormSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "14px",
});

const ChangePasswordHeader = styled(Box)(({ theme }) => ({
  padding: "8px 4px",
  borderBottom: "1px solid #35d7e4",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "824px",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
  },
}));

const SectionTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "18px",
  fontWeight: 600,
  color: "#065b70",
  lineHeight: "normal",
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "14px",
  fontWeight: 500,
  color: "#065b70",
  lineHeight: "normal",
});

const ChangePassword: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const score = [
      minLength,
      hasUppercase,
      hasLowercase,
      hasNumbers,
      hasSpecialChar,
    ].filter(Boolean).length;
    return {
      score,
      minLength,
      hasUppercase,
      hasLowercase,
      hasNumbers,
      hasSpecialChar,
    };
  };

  const passwordValidation = validatePassword(newPassword);
  const isPasswordValid =
    passwordValidation.minLength && passwordValidation.score >= 3;
  const doPasswordsMatch =
    newPassword === confirmPassword && confirmPassword !== "";
  const isFormValid = isPasswordValid && doPasswordsMatch;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!localStorage.getItem("access_token")) {
      navigate("/forgot-password");
      return;
    }
    if (!isFormValid || !currentPassword) return;

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      setSuccess("Password has been changed successfully!");
      setTimeout(() => {
        navigate("/reset-password-success", {
          state: { currentView: "changepassword" },
        });
      }, 1500);
    } catch (err: any) {
      let errorMessage = "Password reset failed. Please try again.";
      try {
        const parsed = JSON.parse(err.message);
        errorMessage = parsed?.detail || parsed?.message || errorMessage;
      } catch {
        errorMessage = err.message || errorMessage;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <HeaderTitle>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M12 7.5C9.51472 7.5 7.5 9.51472 7.5 12C7.5 14.4853 9.51472 16.5 12 16.5C13.3488 16.5 14.559 15.9066 15.3838 14.9666C16.0787 14.1745 16.5 13.1365 16.5 12C16.5 11.5401 16.431 11.0962 16.3028 10.6783C15.7382 8.838 14.0253 7.5 12 7.5ZM9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12ZM19.7093 20.3947L17.9818 19.6364C17.4876 19.4197 16.9071 19.4514 16.44 19.7219C15.9729 19.9923 15.675 20.4692 15.6157 21.0065L15.408 22.8855C15.3651 23.2729 15.084 23.5917 14.7055 23.6819C12.9263 24.106 11.0725 24.106 9.29326 23.6819C8.91476 23.5917 8.63363 23.2729 8.59081 22.8855L8.38343 21.0093C8.32251 20.473 8.0112 19.9976 7.54452 19.728C7.07783 19.4585 6.51117 19.4269 6.01859 19.6424L4.29071 20.4009C3.93281 20.558 3.51493 20.4718 3.24806 20.1858C2.00474 18.8536 1.07924 17.2561 0.541215 15.5136C0.425331 15.1383 0.559224 14.7307 0.8749 14.4976L2.40219 13.3703C2.83721 13.05 3.09414 12.5415 3.09414 12.0006C3.09414 11.4597 2.83721 10.9512 2.40162 10.6305L0.875288 9.50507C0.559144 9.27196 0.425127 8.86375 0.54142 8.48812C1.08038 6.74728 2.00637 5.15157 3.24971 3.82108C3.51684 3.53522 3.93492 3.44935 4.29276 3.60685L6.01296 4.36398C6.50793 4.58162 7.07696 4.54875 7.54617 4.27409C8.01335 4.00258 8.32437 3.52521 8.38442 2.98787L8.59334 1.11011C8.63697 0.717971 8.92453 0.396974 9.30894 0.311314C10.19 0.114979 11.0891 0.0106508 12.0131 0C12.9147 0.0104117 13.8128 0.114784 14.6928 0.311432C15.077 0.397275 15.3643 0.718169 15.4079 1.11011L15.617 2.98931C15.7116 3.85214 16.4387 4.50566 17.3055 4.50657C17.5385 4.50694 17.769 4.45832 17.9843 4.36288L19.7048 3.60562C20.0626 3.44812 20.4807 3.53399 20.7478 3.81984C21.9912 5.15034 22.9172 6.74605 23.4561 8.48689C23.5723 8.86227 23.4386 9.27022 23.1228 9.50341L21.5978 10.6297C21.1628 10.9499 20.9 11.4585 20.9 11.9994C20.9 12.5402 21.1628 13.0488 21.5988 13.3697L23.1251 14.4964C23.441 14.7296 23.5748 15.1376 23.4586 15.513C22.9198 17.2536 21.9944 18.8491 20.7517 20.1799C20.4849 20.4657 20.0671 20.5518 19.7093 20.3947ZM14.263 20.1965C14.4982 19.4684 14.9889 18.8288 15.6884 18.4238C16.5702 17.9132 17.6536 17.8546 18.5841 18.2626L19.9281 18.8526C20.791 17.8537 21.4593 16.7013 21.8981 15.4551L20.7095 14.5777L20.7086 14.577C19.898 13.9799 19.4 13.0276 19.4 11.9994C19.4 10.9718 19.8974 10.0195 20.7073 9.42265L20.7085 9.4217L21.8957 8.54496C21.4567 7.29874 20.7881 6.1463 19.9248 5.14764L18.5922 5.73419L18.5899 5.73521C18.1844 5.91457 17.7472 6.00716 17.3039 6.00657C15.6715 6.00447 14.3046 4.77425 14.1261 3.15459L14.1259 3.15285L13.9635 1.69298C13.3202 1.57322 12.6677 1.50866 12.013 1.50011C11.3389 1.50885 10.6821 1.57361 10.0377 1.69322L9.87514 3.15446C9.76248 4.16266 9.17931 5.05902 8.30191 5.5698C7.41937 6.08554 6.34453 6.14837 5.40869 5.73688L4.07273 5.14887C3.20949 6.14745 2.54092 7.29977 2.10196 8.54587L3.29181 9.42319C4.11115 10.0268 4.59414 10.9836 4.59414 12.0006C4.59414 13.0172 4.11142 13.9742 3.29237 14.5776L2.10161 15.4565C2.54002 16.7044 3.2085 17.8584 4.07205 18.8587L5.41742 18.2681C6.34745 17.8613 7.41573 17.9214 8.29471 18.4291C9.17398 18.9369 9.75934 19.8319 9.87384 20.84L9.87435 20.8445L10.0362 22.3087C11.3326 22.5638 12.6662 22.5638 13.9626 22.3087L14.1247 20.8417C14.1491 20.6217 14.1955 20.4054 14.263 20.1965Z"
              fill="#065B70"
            />
          </svg>
          <Title>Change Password</Title>
        </HeaderTitle>
      </HeaderContainer>

      <ContentSection>
        <ContentWrapper>
          <FormSection>
            <ChangePasswordHeader>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
                <SectionTitle>Set New Password</SectionTitle>
                <SectionSubtitle>
                  Password must meet the following requirements:
                </SectionSubtitle>
              </Box>
            </ChangePasswordHeader>

            <Box component="ul" sx={{ mb: 0, mt: 0 }}>
              <Typography
                component="li"
                sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "14px", color: "#555" }}
              >
                Minimum 8 characters
              </Typography>
              <Typography
                component="li"
                sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "14px", color: "#555" }}
              >
                At least one uppercase letter (A–Z)
              </Typography>
              <Typography
                component="li"
                sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "14px", color: "#555" }}
              >
                At least one lowercase letter (a–z)
              </Typography>
              <Typography
                component="li"
                sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "14px", color: "#555" }}
              >
                At least one number (0–9)
              </Typography>
              <Typography
                component="li"
                sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "14px", color: "#555" }}
              >
                At least one special character (e.g., !@#$%^&*)
              </Typography>
            </Box>

            <FormContent>
              <Box
                component="form"
                onSubmit={onSubmit}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "35px",
                  width: "100%",
                }}
              >
                <PasswordSection>
                  <SingleInputSection>
                    <InputContainer>
                      <InputLabel>Current Password</InputLabel>
                      <StyledTextField
                        fullWidth
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                      />
                    </InputContainer>
                    <InputContainer>
                      <InputLabel>Enter New Password</InputLabel>
                      <StyledTextField
                        fullWidth
                        type={showNewPassword ? "text" : "password"}
                        variant="outlined"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="*********"
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  setShowNewPassword(!showNewPassword)
                                }
                                edge="end"
                                size="small"
                                sx={{ color: "#9ca3af" }}
                              >
                                {showNewPassword ? (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M12 7C14.7614 7 17 9.23858 17 12C17 14.7614 14.7614 17 12 17C9.23858 17 7 14.7614 7 12C7 9.23858 9.23858 7 12 7Z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                    <path
                                      d="M12 1C18.3939 1 23.7279 5.02369 24 12C23.7279 18.9763 18.3939 23 12 23C5.60607 23 0.272126 18.9763 0 12C0.272126 5.02369 5.60607 1 12 1Z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                    <circle
                                      cx="12"
                                      cy="12"
                                      r="3"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </InputContainer>

                    <PasswordStrengthContainer>
                      <PasswordStrengthBar
                        isActive={passwordValidation.score >= 1}
                      />
                      <PasswordStrengthBar
                        isActive={passwordValidation.score >= 2}
                      />
                      <PasswordStrengthBar
                        isActive={passwordValidation.score >= 3}
                      />
                      <PasswordStrengthBar
                        isActive={passwordValidation.score >= 4}
                      />
                      <PasswordStrengthBar
                        isActive={passwordValidation.score >= 5}
                      />
                    </PasswordStrengthContainer>
                  </SingleInputSection>

                  <InputContainer>
                    <InputLabel>Confirm New Password</InputLabel>
                    <StyledTextField
                      fullWidth
                      type={showConfirmPassword ? "text" : "password"}
                      variant="outlined"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="*********"
                      required
                      error={confirmPassword !== "" && !doPasswordsMatch}
                      helperText={
                        confirmPassword !== "" && !doPasswordsMatch
                          ? "Passwords do not match"
                          : ""
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                              size="small"
                              sx={{ color: "#9ca3af" }}
                            >
                              {showConfirmPassword ? (
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12 7C14.7614 7 17 9.23858 17 12C17 14.7614 14.7614 17 12 17C9.23858 17 7 14.7614 7 12C7 9.23858 9.23858 7 12 7Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                  <path
                                    d="M12 1C18.3939 1 23.7279 5.02369 24 12C23.7279 18.9763 18.3939 23 12 23C5.60607 23 0.272126 18.9763 0 12C0.272126 5.02369 5.60607 1 12 1Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="3"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                </svg>
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </InputContainer>
                </PasswordSection>
              </Box>
            </FormContent>

            {error && (
              <Typography
                color="error"
                variant="body2"
                sx={{ fontFamily: "Rethink Sans, sans-serif", textAlign: "center" }}
              >
                {error}
              </Typography>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start" }}>
              {success && (
                <Typography
                  color="success.main"
                  variant="body2"
                  sx={{ fontFamily: "Rethink Sans, sans-serif", textAlign: "left" }}
                >
                  {success}
                </Typography>
              )}

              <ContinueButton
              type="submit"
              disabled={loading || !isFormValid}
              isActive={isFormValid}
              onClick={onSubmit}
            >
              {loading ? (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <CircularProgress size={20} color="inherit" />
                  RESETTING...
                </Box>
              ) : (
                "CONTINUE"
              )}
            </ContinueButton>
            </Box>
          </FormSection>
        </ContentWrapper>
      </ContentSection>
    </PageContainer>
  );
};

export default ChangePassword;

